'use client';

import { motion, useReducedMotion } from 'motion/react';
import { Link } from '@tanstack/react-router';

import { sitePrimaryNav } from '@/config/site';

const socialLinks = [
  { label: 'GitHub', href: 'https://github.com/alfadlirt' },
  { label: 'Email', href: 'mailto:alfadli.rai@gmail.com' },
  { label: 'LinkedIn', href: 'https://www.linkedin.com/in/alfadliraihan/' },
];

const footerPages = [
  ...sitePrimaryNav,
  { name: 'Services', href: '/services' },
  { name: 'Personal Stuff', href: '/personal-stuff' },
  { name: 'Contact', href: '/contact' },
];

export function SiteFooter() {
  const reducedMotion = useReducedMotion() ?? false;

  return (
    <motion.footer
      initial={{ opacity: 0, y: reducedMotion ? 0 : 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={
        reducedMotion
          ? { duration: 0.2 }
          : { type: 'spring', bounce: 0, duration: 0.6 }
      }
      className="border-border mx-auto w-full max-w-6xl border-t px-6 pb-10 pt-16"
    >
      <div className="mb-12 max-w-2xl">
        <h2 className="font-display text-display-lg text-ink text-balance font-bold leading-tight">
          Let&apos;s build something{' '}
          <span className="text-brand">meaningful</span> together.
        </h2>
        <p className="text-muted-foreground mt-3">
          Targeting Singapore remote full time. Also open to freelance, part
          time, and contracts with real impact.
        </p>
      </div>

      <div className="border-border grid grid-cols-2 gap-8 border-t pt-10 sm:grid-cols-3">
        <div>
          <p className="text-muted-foreground mb-4 font-mono text-xs font-semibold uppercase tracking-widest">
            Pages
          </p>
          <ul className="flex flex-col gap-2">
            {footerPages.map(({ name, href }) => (
              <li key={name}>
                <Link
                  to={href}
                  className="text-foreground hover:text-brand active:text-brand text-sm transition-colors"
                >
                  {name}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <p className="text-muted-foreground mb-4 font-mono text-xs font-semibold uppercase tracking-widest">
            Connect
          </p>
          <ul className="flex flex-col gap-2">
            {socialLinks.map(({ label, href }) => (
              <li key={label}>
                <a
                  href={href}
                  target={href.startsWith('http') ? '_blank' : undefined}
                  rel={
                    href.startsWith('http') ? 'noopener noreferrer' : undefined
                  }
                  className="text-foreground hover:text-brand active:text-brand text-sm transition-colors"
                >
                  {label}
                </a>
              </li>
            ))}
          </ul>
        </div>

        <div className="col-span-2 sm:col-span-1">
          <p className="text-muted-foreground mb-4 font-mono text-xs font-semibold uppercase tracking-widest">
            Stack
          </p>
          <ul className="flex flex-col gap-2">
            {[
              'Kotlin · Java · Spring Boot',
              'Golang · Python · Node.js',
              'TypeScript · Next.js · React · Hono',
              'PostgreSQL · pgvector · Redis · Elasticsearch · RabbitMQ',
              'Docker · AWS · GCP · Microservices · Slack',
              'RAG · LLM Orchestration · LangSmith · Langfuse',
            ].map((stackLine) => (
              <li key={stackLine}>
                <span className="text-muted-foreground font-mono text-xs">
                  {stackLine}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="border-border mt-10 flex flex-col items-start justify-between gap-3 border-t pt-6 sm:flex-row sm:items-center">
        <span className="text-muted-foreground font-mono text-xs">
          © {new Date().getFullYear()} Alfa. All rights reserved.
        </span>
        <div className="flex items-center gap-4">
          <span className="text-muted-foreground font-mono text-xs">
            Jakarta · targeting Singapore
          </span>
          <span className="bg-border h-3 w-px" aria-hidden="true" />
          <span className="text-brand font-mono text-xs">
            GMT+7 · ready for remote
          </span>
        </div>
      </div>
    </motion.footer>
  );
}
